import React, { useState, useEffect } from 'react';
import { Tabs, Form, Input, Select, Button, Table, Space, Popconfirm, message, Col, Drawer, Row } from 'antd';
import { DeleteOutlined, CloseCircleOutlined, EditOutlined } from '@ant-design/icons';
import { metaDataLoad } from '../../../services/admin/adminService';
import { fetchBranchInitialData, saveStoredProcedure, DeleteStoreProcedure, saveApiFunctions, getParametersByGroupId } from '../../../services/componentService.js';

const { TabPane } = Tabs;
const { Option } = Select;

const Configuration = () => {
    const [apiForm] = Form.useForm();
    const [spForm] = Form.useForm();
    const [currentEditingApi, setCurrentEditingApi] = useState(null);
    const [apiData, setApiData] = useState([]);
    const [spData, setSpData] = useState([]);
    const [parameterOptions, setParameterOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    const [apiSearchText, setApiSearchText] = useState('');
    const [spSearchText, setSpSearchText] = useState('');
    // Drawer for adding API and SP
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [drawerType, setDrawerType] = useState('');

    // Fetch metadata for the parameter dropdown
    useEffect(() => {
        const fetchPopulateData = async () => {
            setLoading(true);
            try {
                const response = await metaDataLoad();
                if (response !== undefined) {
                    const parameters = response.map(param => ({
                        value: param.parameter_name,
                        label: param.parameter_name,
                    }));
                    setParameterOptions(parameters);
                }
            } catch (error) {
                message.error('Error fetching parameter data');
                console.error('Error fetching parameter data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchPopulateData();
    }, []);

    // Fetch API and SP data
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await fetchBranchInitialData();
                if (response?.data) {
                    const apiFunctions = response.data.functionName.map(func => ({
                        key: func.functionCallListId,
                        name: func.apiFunctionName,
                        params: func.parameter,
                        parameterGroupId: func.parameter_group_id
                    }));
                    setApiData(apiFunctions);

                    const spFunctions = response.data.spList.map(func => ({
                        key: func.storeProcedureListId,
                        name: func.list,
                        params: '',
                    }));
                    setSpData(spFunctions);
                }
            } catch (error) {
                message.error('Error fetching data');
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const openDrawer = (type) => {
        setDrawerType(type);
        setDrawerVisible(true);
    }

    const closeDrawer = () => () => {
        setDrawerVisible(false);
    }

    const updateApiList = async (key) => {
        const values = await apiForm.validateFields();
        const apiParams = Array.isArray(values.apiParams) ? values.apiParams : [];
        const newData = {
            key: key,
            name: values.apiName,
            params: apiParams,
        };
        if (newData) {
            const response = await saveApiFunctions(newData);
            if (response.success && response.data && response.data.isExists === 'false') {
                message.success('API function updated successfully.');
                setApiData([...apiData, newData]);
                apiForm.resetFields();
            } else {
                message.error('Failed to add API. It may already exist.');
            }
        }
    }

    const handleAddAPI = async () => {
        try {
            updateApiList(0);
            openDrawer('sp');
        } catch (error) {
            message.error('Please fill in all required fields!');
            console.error('Validation or API error:', error);
        }
    };

    const handleUpdateAPI = async () => {
        try {
            updateApiList(currentEditingApi.key);
            closeDrawer();
        }
        catch (error) {
            message.error('Please fill in all required fields!');
            console.error('Validation error:', error);
        };
    };
    const handleApiEdit = async (record) => {
        var params = [];
        const response = await getParametersByGroupId(record.parameterGroupId);
        if (response.success && response.data) {
            params = response.data;
        }
        setCurrentEditingApi(record);
        apiForm.setFieldsValue({
            apiName: record.name,
            apiParams: params
        });

        openDrawer('api');
    };

    const clearApifields = () => {
        setCurrentEditingApi(null);
        apiForm.resetFields();
    }

    const handleDelete = (key, type) => {
        if (type === 'api') {
            setApiData(apiData.filter(item => item.key !== key));
        } else {
            setSpData(spData.filter(item => item.key !== key));
        }
    };

    const handleAddSP = async () => {
        try {
            const values = await spForm.validateFields(['spName']);
            const storeProcedureName = values.spName;
            if (storeProcedureName != "" && storeProcedureName != undefined) {
                const response = await saveStoredProcedure({ storeProcedureName });
                if (response.success && response.data && response.data.isExists === 'false') {
                    message.success('Stored procedure added successfully.');
                    const updatedResponse = await fetchBranchInitialData();
                    if (updatedResponse?.data) {
                        const spFunctions = updatedResponse.data.spList.map(func => ({
                            key: func.storeProcedureListId,
                            name: func.list,
                            params: '',
                        }));
                        setSpData(spFunctions);
                    }
                    spForm.resetFields();
                } else {
                    message.error('Failed to add stored procedure. It may already exist.');
                }
            }
        } catch (error) {
            message.error('Please input SP name!');
        }
    };

    const handleSpDelete = async (storeProcedureListId) => {
        try {
            const response = await DeleteStoreProcedure(storeProcedureListId);
            if (response.success) {
                spForm.resetFields();
                message.success('Stored procedure deleted successfully');
                setSpData(prevData => prevData.filter(item => item.key !== storeProcedureListId));
            } else {
                message.error('Failed to delete stored procedure');
            }
        } catch (error) {
            message.error('Error deleting stored procedure');
            console.error('Error deleting stored procedure:', error);
        }
    };

    const handleApiSearchClear = () => setApiSearchText('');
    const handleSpSearchClear = () => setSpSearchText('');

    const filteredApiData = apiData.filter(item => item.name.toLowerCase().includes(apiSearchText.toLowerCase()));
    const filteredSpData = spData.filter(item => item.name.toLowerCase().includes(spSearchText.toLowerCase()));

    const apiColumns = [
        { title: 'Id', dataIndex: 'key', key: 'key', hidden: true },
        { title: 'API Name', dataIndex: 'name', key: 'name' },
        { title: 'Parameters', dataIndex: 'params', key: 'params', hidden: true },
        { title: 'Parameter Group Id', dataIndex: 'parameterGroupId', key: 'parameterGroupId', hidden: true },
        { title: 'Description', dataIndex: 'apiDescription', key: 'apiDescription'},
        {
            title: 'Action',
            key: 'action',
            width: '100px',
            render: (text, record) => (
                <Space size="middle">
                    <Button icon={<EditOutlined />} onClick={() => handleApiEdit(record)} />
                    <Popconfirm title="Are you sure you want to delete this record?" okText="Yes" cancelText="No" onConfirm={() => handleDelete(record.key, 'api')}>
                        <Button icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const spColumns = [
        { title: 'SP Name', dataIndex: 'name', key: 'name' },
        { title: 'Description', dataIndex: 'spDescription', key: 'spDescription'},
        {
            title: 'Action',
            key: 'action',
            width: '100px',
            render: (text, record) => (
                <Space size="middle">
                    <Popconfirm title="Are you sure you want to delete this record?" okText="Yes" cancelText="No" onConfirm={() => handleSpDelete(record.key)}>
                        <Button icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <h2 style={{ marginBottom: '7px' }}>Configuration</h2>
            <Tabs defaultActiveKey="1">

                <TabPane tab="API Configuration" key="1">
                    <Row gutter={20}>
                        <Col span={8}>
                            <Button style={{ marginBottom: '10px' }} type="primary" onClick={() => { openDrawer('api'); clearApifields(); }}>Add API</Button>
                        </Col>
                        <Col span={8} align='right' offset={8}>
                            <Input
                                className='cus-antd-white'
                                placeholder="Search API name"
                                value={apiSearchText}
                                onChange={e => setApiSearchText(e.target.value)}
                                style={{ maxWidth: '240px' }}
                            />
                            <Button type='text' shape='default' onClick={handleApiSearchClear}>
                                <CloseCircleOutlined />
                            </Button>
                        </Col>
                    </Row>
                    <Row gutter={20}>
                        <Col span={24}>
                            <Table
                                className='cus-antd-white'
                                bordered={true}
                                columns={apiColumns}
                                dataSource={filteredApiData}
                                pagination={{ pageSize: 9, showSizeChanger: false }}
                            />
                        </Col>
                    </Row>
                </TabPane>


                <TabPane tab="SP Configuration" key="2">
                    <Row gutter={20}>
                        <Col span={8}>
                            <Button style={{ marginBottom: '10px' }} type="primary" onClick={() => { openDrawer('sp'); clearApifields(); }}>Add SP</Button>
                        </Col>
                        <Col span={8} align='right' offset={8}>
                            <Form.Item>
                                <Input
                                    className='cus-antd-white'
                                    layout='vertical'
                                    placeholder="Search SP name"
                                    value={spSearchText}
                                    onChange={e => setSpSearchText(e.target.value)}
                                    style={{ maxWidth: '240px' }}
                                />

                                <Button type='text' shape='default' onClick={handleSpSearchClear}>
                                    <CloseCircleOutlined />
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={20}>
                        <Col span={24}>
                            <Table
                                className='cus-antd-white'
                                bordered={true}
                                columns={spColumns}
                                dataSource={filteredSpData}
                                pagination={{ pageSize: 9, showSizeChanger: false }}
                            />
                        </Col>
                    </Row>
                </TabPane>
            </Tabs>
            <Drawer
                className="custom-drawers cus-drawer-sm"
                style={{ width: "375px !important;" }}
                title={drawerType === 'api' ? 'Add API Configuration' : 'Add SP Configuration'}
                onClose={() => setDrawerVisible(false)} open={drawerVisible}
                footer={drawerType === 'api' ? (
                    <>
                        {currentEditingApi ? (
                            <>
                                <div style={{ textAlign: 'right' }}>
                                    <Button type="default" onClick={closeDrawer()} style={{ marginRight: 8 }}>
                                        Clear
                                    </Button>
                                    <Button type="primary" onClick={handleUpdateAPI}>
                                        Update API
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <div style={{ textAlign: 'right' }}>
                                <Button type="default" onClick={clearApifields} style={{ marginRight: 8 }}>
                                    Clear
                                </Button>
                                <Button type="primary" onClick={handleAddAPI}>
                                    Add API
                                </Button>
                            </div>
                        )}
                    </>) :
                    (
                        <div style={{ textAlign: 'right' }}>
                            <Button type="default" onClick={closeDrawer()} style={{ marginRight: 8 }}>
                                Clear
                            </Button>
                            <Button type="primary" onClick={handleAddSP}>
                                Add SP
                            </Button>
                        </div>
                    )}
            >
                {drawerType === 'api' ? (

                    <Form form={apiForm} layout="inline">
                        <Row gutter={20}>
                            <Col span={24}>
                                <Row gutter={20}>
                                    <Col span={24}>
                                        <Form.Item
                                            name="apiName"
                                            layout='vertical'
                                            label="API Name"
                                            rules={[{ required: true, message: 'Please input API name!' }]}
                                        >
                                            <Input width={'100%'} placeholder="Enter API name" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col span={24}>
                                        <Form.Item
                                            name="apiParams"
                                            layout='vertical'
                                            label="Parameters"
                                            rules={[{ required: true, message: 'Please select parameters!' }]}
                                        >
                                            <Select
                                                mode="multiple"
                                                placeholder="Select parameters"
                                                style={{ width: '300px' }}
                                                maxTagCount={1}
                                                maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
                                                loading={loading}
                                            >
                                                {parameterOptions.map((param) => (
                                                    <Option key={param.value} value={param.value}>
                                                        {param.label}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Form>
                ) : (
                    <Form form={spForm}>
                        <Row gutter={20} span={24}>
                            <Col span={24}>
                                <Form.Item
                                    name="spName"
                                    label="SP Name"
                                    layout='vertical'
                                    rules={[{ required: true, message: 'Please input SP name!' }]}
                                >
                                    <Input width={'100%'} placeholder="Enter SP name" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                )}
            </Drawer>
        </div >
    );
};

export default Configuration;
